/**
 * Clickable column header that cycles asc → desc → default. Server-side
 * sort lives in the backend's SORT_COLUMNS whitelist; this component
 * only emits the `sort_by` key + direction the parent decides on.
 *
 * Visual state:
 *   - inactive   → just the label
 *   - asc        → label + ↑ + bold
 *   - desc       → label + ↓ + bold
 *
 * Uses a plain <button> rather than Polaris Button so it sits inline
 * inside an IndexTable header cell without layout fights.
 */

export interface SortableHeaderProps {
  label: string;
  /** Identifier the parent passes to its API call (e.g. "brand"). */
  sortKey: string;
  /** Currently active sort key (one column at a time). */
  activeKey: string;
  activeDir: 'asc' | 'desc';
  /** Sort key to fall back to on the third click. */
  defaultKey?: string;
  onChange: (next: { key: string; dir: 'asc' | 'desc' }) => void;
}

export function SortableHeader({
  label, sortKey, activeKey, activeDir, defaultKey = 'brand', onChange,
}: SortableHeaderProps) {
  const isActive = activeKey === sortKey;
  const arrow = !isActive ? '' : activeDir === 'asc' ? ' ↑' : ' ↓';

  const handleClick = () => {
    if (!isActive) {
      onChange({ key: sortKey, dir: 'asc' });
    } else if (activeDir === 'asc') {
      onChange({ key: sortKey, dir: 'desc' });
    } else {
      onChange({ key: defaultKey, dir: 'asc' });
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        fontWeight: isActive ? 600 : 500,
        textAlign: 'left',
      }}
    >
      {label}{arrow}
    </button>
  );
}
